import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { xorBy } from 'lodash';
import dealerGroupDeleteProductEnhancementDocumentApi from 'models/dealerManagement/dealerGroup/deleteProductEnhancementDocument/Api.DealerGroup.DeleteProductEnhancementDocument';
import productEnhancementDocumentListDealerGroupDocumentsApi from 'models/productManagement/productEnhancementDocument/listDealerGroups/Api.ProductEnhancementDocument.ListDealerGroups';
import { useEffect, useMemo, useState } from 'react';
import { productEnhancementIndexRoute } from '../../productEnhancement/ProductEnhancement.Index';
import { ProductEnhancementDealerGroupDocumentAddForm } from './sections/ProductEnhancementDocument.DealerGroupDocuments.Add';
import dealerGroupUploadProductEnhancementDocumentApi from 'models/dealerManagement/dealerGroup/uploadProductEnhancementDocument/Api.DealerGroup.UploadProductEnhancementDocument';

export enum ProductEnhancementDealerGroupDocumentsAction {
  ADD = 'add',
  DELETE = 'delete',
}
export type VerifyProductDocumentsAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof ProductEnhancementDealerGroupDocumentsAction>
>;
export default function useProductEnhancementDealerGroupDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductEnhancementDocument.Params,
    Module.ProductManagement.ProductEnhancementDocument.Query
  >({
    route: productEnhancementIndexRoute,
  });
  const productEnhancementDocumentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const { lang } = getLocales();
  const [currentView, setView] = useState<ProductEnhancementDealerGroupDocumentsAction | undefined>();
  const [selectedItems, setSelectedItems] = useState<Model.IProductEnhancementDealerGroupDocument[]>([]);
  const [{ limit, offset, distributorId }, setDealerGroupDocumentFilters] = useState({
    limit: defaultLimit,
    offset: defaultOffset,
    distributorId: undefined,
  });

  const canView = permissions.EDIT_PRODUCT;
  const listDealerGroupDocumentsApi = useApi({
    action: productEnhancementDocumentListDealerGroupDocumentsApi,
    default: { productEnhancementDocumentId: productEnhancementDocumentId },
    body: { productEnhancementDocumentId } as Api.Core.ProductEnhancementDocument.ListDealerDocuments.IRequest,
    wait: !canView,
  });

  const handleUploadDealerGroupDocumentSubmit = async (formData: ProductEnhancementDealerGroupDocumentAddForm) => {
    const dealerGroups = formData.dealerGroups || [];
    let uploadedFiles = 0;

    for (const dealerGroup of dealerGroups) {
      const docUploadResponse = await dealerGroupUploadProductEnhancementDocumentApi(
        {
          id: dealerGroup,
          productEnhancementId: formData.productEnhancementId,
          productEnhancementDocumentId: formData.productEnhancementDocumentId,
        },
        { formFile: formData.formFile }
      );

      if (docUploadResponse.status === HttpStatus.ok) {
        uploadedFiles += 1;
      }
    }

    listDealerGroupDocumentsApi.execute((body) => body);

    if (dealerGroups.length !== uploadedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get uploaded!`,
          dealerGroups.length > 0 ? `${uploadedFiles}/${dealerGroups.length} files uploaded!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyUploaded, {
          entity: lang.productEnhancementDocuments,
        }),
      });
    }

    setView(undefined);
  };

  const handleDeleteDealerGroupDocumentSubmit = async () => {
    const productEnhancementDealerGroupDocuments = selectedItems || [];
    let deletedFiles = 0;

    for (const productEnhancementDealerGroupDocument of productEnhancementDealerGroupDocuments) {
      const docDeleteReponse = await dealerGroupDeleteProductEnhancementDocumentApi({
        productEnhancementDealerGroupDocumentId: productEnhancementDealerGroupDocument.id,
      });
      if (docDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
    }

    listDealerGroupDocumentsApi.execute((body) => body);

    if (productEnhancementDealerGroupDocuments.length !== deletedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get deleted!`,
          productEnhancementDealerGroupDocuments.length > 0
            ? `${deletedFiles}/${productEnhancementDealerGroupDocuments.length} files deleted!`
            : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyDeleted, {
          entity: lang.productEnhancementDocuments,
        }),
      });
    }

    setView(undefined);
  };

  const toggleItem = (productEnhancementDealerGroupDocument: Model.IProductEnhancementDealerGroupDocument) => {
    const newSelectedItemList = xorBy(selectedItems || [], [productEnhancementDealerGroupDocument], 'dealerGroupId');
    setSelectedItems(newSelectedItemList);
  };

  const toggleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedItems(listDealerGroupDocumentsApi.payload?.data ?? []);
    } else {
      setSelectedItems([]);
    }
  };

  const [filteredDealerGroupDocuments, filteredDealerGroupDocumentsCount] = useMemo(() => {
    const filteredList = listDealerGroupDocumentsApi.payload?.data?.filter(
      (dealerGroupDocument) => !distributorId || dealerGroupDocument.dealerGroup.distributorId === distributorId
    );

    return [filteredList?.slice(offset * limit, offset * limit + limit), filteredList?.length];
  }, [listDealerGroupDocumentsApi.payload, limit, offset, distributorId]);

  useEffect(() => {
    setSelectedItems([]);
  }, [listDealerGroupDocumentsApi.payload?.data]);

  return {
    currentView,
    setView,
    selectedItems,
    toggleItem,
    toggleSelectAll,
    handleDeleteDealerGroupDocumentSubmit,
    handleUploadDealerGroupDocumentSubmit,
    permissions,
    fullDealerGroupDocumentList: listDealerGroupDocumentsApi?.payload?.data,
    dealerGroupDocumentList: {
      limit,
      offset,
      distributorId,
      setDealerGroupDocumentFilters,
      data: filteredDealerGroupDocuments,
      count: filteredDealerGroupDocumentsCount,
      applyFilters: ({ distributorId }: { distributorId?: number }) => {
        setDealerGroupDocumentFilters((old) => ({ ...old, distributorId }));
      },
      resetFilters: () => {
        setDealerGroupDocumentFilters({ limit: defaultLimit, offset: defaultOffset, distributorId: undefined });
      },
    },
    views: {
      [ProductEnhancementDealerGroupDocumentsAction.DELETE]:
        currentView === ProductEnhancementDealerGroupDocumentsAction.DELETE,
      [ProductEnhancementDealerGroupDocumentsAction.ADD]:
        currentView === ProductEnhancementDealerGroupDocumentsAction.ADD,
    },
  };
}

export function useProductEnhancementDealerGroupDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductEnhancementDealerGroupDocument>>(
    'productEnhancementDealerGroupDocumentHook'
  );
}
